// One row in the /diaries feed.
//
// W40.3. Split out of `routes/Diaries.tsx`, which had drifted to 16.8 KB
// against a 15 KB ceiling and was passing `route-size-ceiling.test.ts` on the
// ratchet's 256-byte slack with 51 bytes to spare — one comment added by the
// next person and the gate goes red. The rule (AGENTS.md §3 #10) says the
// remedy for an over-ceiling route component is to split it, not to re-baseline
// it, so the card moved here and the `KNOWN_DEBT` entry was deleted rather
// than re-measured. A baseline that only ever grows stops measuring anything.
//
// The card is presentational: it takes a diary and whether the viewer wrote it,
// and renders the same markup it always did. The `preview` is derived here
// rather than in the list because `previewFromHtml` is pure and the list was
// computing the identical string for every row on every render.

import { Link } from "react-router-dom";

import { Card } from "@/components/ui/card";
import { DiaryLikeButton } from "@/components/diary/DiaryLikeButton";
import { cn } from "@/lib/utils/cn";
import { emojiForCode } from "@/lib/diary/mood";
import { formatRelative } from "@/lib/utils/date";
import { previewFromHtml } from "@/lib/utils/text";
import { mediaFileUrl } from "@/lib/api/media";
import type { DiaryOut } from "@/lib/api/diary";

interface DiaryCardProps {
  diary: DiaryOut;
  /** true when the signed-in user wrote it — drives the author chip tint. */
  isAuthor: boolean;
}

export function DiaryCard({ diary: d, isAuthor }: DiaryCardProps) {
  const preview = previewFromHtml(d.content, 140);

  return (
    <Link
      to={`/diaries/${d.id}`}
      data-testid={`diary-row-${d.id}`}
      className="block press focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2 rounded-2xl"
    >
      <Card>
        <div className="p-s-4 flex flex-col gap-s-3">
          <div className="flex items-center justify-between gap-s-2 text-meta text-ink-3">
            <div className="flex items-center gap-s-2 min-w-0">
              <span className="tabular-nums">{d.date}</span>
              <span
                className={cn(
                  "rounded-pill px-2 py-0.5 text-meta font-semibold",
                  isAuthor ? "bg-you-tint text-you-strong" : "bg-partner-tint text-partner-strong",
                )}
                data-testid="diary-row-author-chip"
                data-author={isAuthor ? "self" : "partner"}
              >
                {isAuthor ? "你寫的" : "對方寫的"}
              </span>
              {d.is_private ? (
                <span className="rounded-pill bg-paper-deep px-2 py-0.5 text-meta text-ink-2">
                  私人
                </span>
              ) : null}
            </div>
            {d.mood ? (
              <span aria-label="mood" className="text-title-3 shrink-0">
                {emojiForCode(d.mood)}
              </span>
            ) : null}
          </div>
          {d.title ? (
            <h2 className="font-display text-title-3 text-ink-1 truncate">{d.title}</h2>
          ) : null}
          {preview ? (
            <p className="text-body text-ink-2 line-clamp-3 whitespace-pre-wrap">{preview}</p>
          ) : null}
          {d.images.length > 0 ? (
            <div className="grid grid-cols-3 gap-1.5 mt-1" data-testid="diary-card-thumbs">
              {d.images.slice(0, 3).map((img) => (
                <div
                  key={img.id}
                  className="aspect-square rounded-md overflow-hidden bg-paper-deep"
                >
                  <img
                    src={mediaFileUrl(img.media_id)}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
              {d.images.length > 3 ? (
                <div className="aspect-square rounded-md bg-paper-deep flex items-center justify-center text-body-sm text-ink-2">
                  +{d.images.length - 3}
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex items-center justify-between text-meta text-ink-3">
            <span>{formatRelative(new Date(d.created_at))}</span>
            <DiaryLikeButton diaryId={d.id} />
          </div>
        </div>
      </Card>
    </Link>
  );
}
